import { createContext, useMemo, useState } from "react";
import { Artifact } from "../models/client";

interface ArtifactViewSelectionProviderProps {
  availableArtifacts: Artifact[];
}

interface ArtifactViewSelectionContextValue {
  selectedArtifact: Artifact | null;
  setArtifactById: (artifactId: number | null) => void;
  nextArtifact: () => void;
}

export const ArtifactViewSelectionContext =
  createContext<ArtifactViewSelectionContextValue>(
    {} as ArtifactViewSelectionContextValue
  );

export const ArtifactViewSelectionProvider: React.FC<
  ArtifactViewSelectionProviderProps
> = ({ children, availableArtifacts }) => {
  const [selectedArtifact, setArtifact] = useState<Artifact | null>(null);

  const setArtifactById = useMemo(() => {
    return (id: number | null) => {
      const matched =
        availableArtifacts.find((artifact) => artifact.id === id) || null;
      setArtifact(matched);
    };
  }, [availableArtifacts]);

  const nextArtifact = useMemo(() => {
    return () => {
      if (!selectedArtifact) {
        return;
      }
      const matched = availableArtifacts.findIndex(
        (a) => a.id === selectedArtifact.id
      );
      setArtifact(availableArtifacts.at(matched + 1) || null);
    };
  }, [availableArtifacts, selectedArtifact]);

  const value = useMemo(
    () => ({ selectedArtifact, setArtifactById, nextArtifact }),
    [selectedArtifact, setArtifactById, nextArtifact]
  );

  return (
    <ArtifactViewSelectionContext.Provider value={value}>
      {children}
    </ArtifactViewSelectionContext.Provider>
  );
};
